import React from 'react';
import styled from 'styled-components';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { MediumButton, MediumPrimaryButton } from '../../../components/Buttons';
import { ModalContainer, ModalBody, ModalHeader, ModalFooter } from '../../../components/ModalElements';
import { Label, FormField } from '../../../components/FormElements';
import { FileUploader, UploadedFile } from '../../../components/fileUploader';

type Downloadable = {
  fileName: string;
  field: {
    id: string;
    ext: string;
  };
};

type Supply = {
  name?: string;
  quantity?: string;
  supplyType?: string;
  downloadable?: Downloadable | null;
};

type AddSupplyModalProps = {
  supply?: Supply;
  onSubmit: (supply: Supply) => void;
  handleDismiss: () => void;
};

type FormValues = {
  name: string;
  quantity: string;
  file?: Downloadable | null;
};

type SetFieldValue = FormikHelpers<FormValues>['setFieldValue'];

const SupplyContainer = styled.div`
  > div {
    margin: 0;
  }
`;
const AddSupplyModal = ({ supply = {}, onSubmit, handleDismiss }: AddSupplyModalProps) => {
  const supplyName = supply?.name || '';
  const quantity = supply?.quantity ?? '';
  const downloadable = supply?.downloadable ?? null;

  const transformDownloadableToFile = (downloadable?: Downloadable | null) =>
    downloadable && { name: downloadable.fileName };
  const transformFileToDownloadable = (file?: UploadedFile) =>
    file && { fileId: { id: file.id, ext: file.extension }, fileName: file.name };

  const setFileFieldValue = (setFieldValue: SetFieldValue) => (file?: UploadedFile) =>
    setFieldValue('file', transformFileToDownloadable(file));

  const addSupply = ({ name, quantity, file }: FormValues) =>
    onSubmit({
      name,
      quantity,
      downloadable: file,
      supplyType: 'craft',
    });

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader data-qa-hook="modalHeader">
        <h3>{supplyName ? 'Edit Supply' : 'Add Supply'}</h3>
      </ModalHeader>
      <Formik<FormValues>
        initialValues={{
          name: supplyName,
          quantity: quantity,
          file: downloadable,
        }}
        enableReinitialize={true}
        validationSchema={Yup.object({
          name: Yup.string().max(64, 'Name is too long').required('A name is required'),
          quantity: Yup.string().max(128, 'Quantity is too long').required('Quantity is required'),
        })}
        onSubmit={addSupply}
      >
        {({ values, isSubmitting, dirty, setFieldValue }) => (
          <Form id="add-session-form">
            <ModalBody className="grid-container grid-row-gap-24">
              <div className="grid-container grid-col-6 grid-col-gap-24 grid-row-gap-8">
                <SupplyContainer className="grid-col-span-6 grid-sm-col-span-3">
                  <FormField
                    data-qa-hook="addSupplyName"
                    label="Supply Name"
                    name="name"
                    type="text"
                    placeholder="Enter Supply Name"
                  />
                </SupplyContainer>
                <div className="grid-col-span-6 grid-sm-col-span-3">
                  <FormField
                    data-qa-hook="quantity"
                    label="Quantity"
                    name="quantity"
                    value={`${values.quantity}`}
                    placeholder="Enter Quantity"
                  />
                </div>
              </div>
              <div>
                <Label>Attach Resource (optional)</Label>
                <FileUploader
                  initialFile={transformDownloadableToFile(values.file)}
                  onFileUploaded={setFileFieldValue(setFieldValue)}
                  onFileRemoved={setFileFieldValue(setFieldValue)}
                  uploadLocation="curriculum-storage/files"
                />
              </div>
            </ModalBody>
            <ModalFooter>
              <MediumPrimaryButton
                data-qa-hook="modalConfirm"
                type="submit"
                disabled={isSubmitting || (supplyName && !dirty)}
                operating={isSubmitting}
              >
                {supplyName ? 'Save Supply' : 'Add Supply'}
              </MediumPrimaryButton>
              <MediumButton onClick={handleDismiss}>Cancel</MediumButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </ModalContainer>
  );
};

export default AddSupplyModal;
